import type { ReactNode } from "react";
import { FileText, Link2, Settings2 } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { getString } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getLookupSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const joinType = getString(config.joinType) || "left";
  const joinKeys = Array.isArray(config.joinKeys)
    ? (config.joinKeys as { sourceField: string; lookupField: string }[])
    : [];
  const selectColumns = Array.isArray(config.selectColumns) ? (config.selectColumns as string[]) : [];
  const cacheStrategy = getString(config.cacheStrategy) || "full";

  items.push(
    <StepInfoRow key="join" icon={<Link2 className="size-3.5" />} label={t("summaries.lookup.label_join")} value={`${joinType.toUpperCase()} JOIN`} />
  );

  if (joinKeys.length > 0) {
    const keysStr = joinKeys.map((k) => `${k.sourceField} → ${k.lookupField}`).join(", ");
    items.push(
      <StepInfoRow key="keys" icon={<FileText className="size-3.5" />} label={t("summaries.lookup.label_keys")} value={keysStr} mono />
    );
  }

  if (selectColumns.length > 0) {
    const maxDisplay = 3;
    const displayCols = selectColumns.slice(0, maxDisplay).join(", ");
    const remaining = selectColumns.length - maxDisplay;
    const colsValue = remaining > 0 ? `${displayCols} ${t("summaries.lookup.more_columns", { count: remaining })}` : displayCols;
    items.push(
      <StepInfoRow
        key="select"
        icon={<FileText className="size-3.5" />}
        label={t("summaries.lookup.label_enrich")}
        value={<span title={selectColumns.join(", ")}>{colsValue}</span>}
      />
    );
  }

  const cacheLabels: Record<string, { labelKey: string; color: string }> = {
    full: { labelKey: "summaries.lookup.cache_full", color: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" },
    lru: { labelKey: "summaries.lookup.cache_lru", color: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
    none: { labelKey: "summaries.lookup.cache_none", color: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400" },
  };
  const cacheInfo = cacheLabels[cacheStrategy] || cacheLabels.full;

  items.push(
    <div key="cache" className="mt-1 flex items-center gap-2 border-t border-gray-100 pt-1 text-xs dark:border-gray-800">
      <span className="shrink-0 text-gray-400"><Settings2 className="size-3.5" /></span>
      <div className="min-w-0 flex-1">
        <span className="text-gray-500 dark:text-gray-400">{t("summaries.lookup.label_cache")}: </span>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cacheInfo.color}`}>
          {t(cacheInfo.labelKey)}
        </span>
      </div>
    </div>
  );

  return items;
}
